import React from "react";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ShieldCheckIcon, MoneyIcon, ReceiptIcon, IdentificationCardIcon, BuildingsIcon,
  CalendarCheckIcon, WalletIcon, PlugsIcon, CloudArrowUpIcon, QrCodeIcon, UsersThreeIcon,
  ClockCounterClockwiseIcon, UserPlusIcon, StackIcon, PackageIcon, ForkKnifeIcon,
  BedIcon, CoinsIcon, ClockCountdownIcon, CameraIcon, GearSixIcon,
  MartiniIcon, ConfettiIcon, StarIcon, FileTextIcon, ChartLineIcon,
  ArrowClockwiseIcon, WhatsappLogoIcon, EnvelopeIcon, CrownSimpleIcon, TrashIcon, WarningIcon, DeviceMobileIcon,
} from "@phosphor-icons/react";

const Section = ({ icon: Icon, title, badge, children }) => (
  <Card className="p-6">
    <div className="flex items-center gap-2 mb-3">
      <Icon size={20} weight="duotone" className="text-primary" />
      <h3 className="font-display text-lg">{title}</h3>
      {badge && <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/30 ml-1">{badge}</Badge>}
    </div>
    <div className="text-sm text-muted-foreground space-y-2 leading-relaxed">{children}</div>
  </Card>
);

const Formula = ({ children }) => (
  <code className="block bg-muted/60 rounded-md px-3 py-2 mt-1 mb-1 font-mono-plex text-[12px] text-foreground border border-border">
    {children}
  </code>
);

const Kbd = ({ children }) => (
  <span className="inline-block rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-mono-plex mx-0.5">{children}</span>
);

export default function Help() {
  return (
    <div>
      <PageHeader
        overline="Guide"
        title="Help & Documentation"
        description="How every module in the PMS works — reservations, meal plans, POS, halls, quotations, backups, reports, offline sync, and role permissions. Bookmark this page for training new staff."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section icon={BedIcon} title="Reservations & Front Desk" badge="Primary">
          <p>The Front Desk (<Kbd>/timeline</Kbd>) is the main working screen. It shows a visual grid of rooms × days.</p>
          <ul className="list-disc pl-5 space-y-1 text-xs">
            <li><b>Today's Actions strip</b> — arrivals get a Check-in button; departures + in-house get "C-out & Bill" with a <b>↺ Undo</b> shortcut for mistakes.</li>
            <li>Click an <b>empty cell</b> to quick-book a room. Pick guest, dates, adults + children, <b>meal plan</b> (EP / CP / MAP / AP), <b>extra beds</b>, <b>CNB</b>, and check "complimentary early / late check-out" if applicable.</li>
            <li>Business rules enforced: check-out must be at least 1 day after check-in; guest details required; no double-booking on same room + overlapping dates.</li>
            <li>Click an existing reservation bar to edit dates, change plan, move room, cancel, or delete.</li>
          </ul>
        </Section>

        <Section icon={ReceiptIcon} title="Check-out & Bill (Front Desk)" badge="New">
          <p>Click <b>C-out &amp; Bill</b> on any checked-in reservation. A dialog opens with:</p>
          <ul className="list-disc pl-5 space-y-1 text-xs">
            <li>Room total (already includes plan + extras from booking).</li>
            <li><b>Extras builder</b> — add mini-bar, laundry, room service, phone with one-tap chips or custom rows.</li>
            <li>Discount, GST rate + inclusive toggle, Corporate GST billing (name / GSTIN / PAN / address).</li>
            <li>Payment mode (cash / UPI / card / bank / corporate credit) + reference.</li>
            <li><b>Live total</b> updates as you type.</li>
          </ul>
          <p>Two save options: <b>Save (unpaid)</b> just generates the bill; <b>Finalise &amp; mark paid</b> also records payment and closes the reservation.</p>
        </Section>

        <Section icon={CoinsIcon} title="Meal Plans (EP / CP / MAP / AP)">
          <p>Every reservation carries a plan code. Rates are set in <b>Admin Console → Meal Plans</b>.</p>
          <Formula>plan_total = plan_rate_per_pax × (adults + children − CNB) × nights</Formula>
          <Formula>extras_total = (extra_bed_rate × extra_beds + cnb_rate × cnb) × nights</Formula>
          <p>Both add to the reservation total automatically — no manual calculation needed. Defaults: CP ₹400, MAP ₹900, AP ₹1500 per pax per night; Extra bed ₹500; CNB ₹300.</p>
        </Section>

        <Section icon={FileTextIcon} title="Quotations" badge="New">
          <p>Send pro-forma quotations from <Kbd>/quotations</Kbd>. Each row can be a <b>Room</b> (with meal plan), <b>Hall/Event</b> (with veg/non-veg plate type), <b>Amenity</b>, or Other.</p>
          <ul className="list-disc pl-5 space-y-1 text-xs">
            <li>Download a branded <b>PDF</b> (<Kbd>PDF</Kbd> button).</li>
            <li>Share via <b>Email</b> <EnvelopeIcon size={11} className="inline" /> or <b>WhatsApp</b> <WhatsappLogoIcon size={11} className="inline text-[hsl(140_60%_35%)]" /> — a personal note can be added; delivery log is stored on the quote.</li>
            <li>Status flow: draft → sent → accepted → converted (or rejected).</li>
          </ul>
        </Section>

        <Section icon={UsersThreeIcon} title="Guest 360 Timeline" badge="New">
          <p>On <Kbd>/guests</Kbd> click the clock icon on any row. Opens a unified profile with 7 KPIs (stays, nights, room spend, F&amp;B, halls, amenities, <b>Lifetime Value</b>) and 6 tabs.</p>
          <ul className="list-disc pl-5 space-y-1 text-xs">
            <li>Timeline — every activity newest-first (stays, invoices, POS orders, halls, amenities).</li>
            <li>Stays · Invoices · F&amp;B · Halls · Amenities — dedicated tabs with tables.</li>
          </ul>
          <p>Great for repeat-customer conversion — spot high-value guests and upsell before their next visit.</p>
        </Section>

        <Section icon={ForkKnifeIcon} title="Restaurant POS + Bar">
          <p>Full POS at <Kbd>/pos</Kbd>. Manage menu, tables (dine-in floor plan), KOT thermal print, raw-ingredient stock + recipe deduction, discounts, per-item GST.</p>
          <ul className="list-disc pl-5 space-y-1 text-xs">
            <li><b>Bill to room</b> — direct any order to a checked-in reservation; it consolidates onto that guest's master invoice.</li>
            <li><b>Bar items sold by peg</b> — bottles are opened, pegs are poured. See variance in Reports → Bar Reconciliation.</li>
            <li><b>Raw ingredients</b> — add Potato, Oil, Salt, etc. and link them to menu-item recipes so stock auto-deducts on every order.</li>
          </ul>
        </Section>

        <Section icon={ConfettiIcon} title="Halls & Events">
          <p>Book banquets, receptions and corporate events at <Kbd>/halls</Kbd>. Two pricing modes:</p>
          <ul className="list-disc pl-5 space-y-1 text-xs">
            <li><b>Fixed</b> — hourly or day rate.</li>
            <li><b>Per plate</b> — veg + non-veg plate counts × plate rates. Live-computed total, warns if below minimum plates.</li>
          </ul>
          <p>Closing a hall booking opens a <b>consolidated invoice</b> combining hall rent + amenity charges + any POS orders posted to the hall + manual items — with split payment support.</p>
        </Section>

        <Section icon={StarIcon} title="Amenities">
          <p>Spa, gym, tours, gaming and rentals live at <Kbd>/amenities</Kbd>. Bookings can be tied to a guest phone so they roll into their Guest 360 and hall invoices.</p>
        </Section>

        <Section icon={ChartLineIcon} title="Reports" badge="Expanded">
          <p>Consolidated at <Kbd>/reports</Kbd>. Tabs:</p>
          <ul className="list-disc pl-5 space-y-1 text-xs">
            <li><b>GST</b> — monthly GST liability + auto CA email schedule.</li>
            <li><b>Daily Sales</b> — rooms / restaurant / halls / amenities / expenses per day for any date range.</li>
            <li><b>Bar Reconciliation</b> — bottles purchased vs pegs poured vs pegs remaining. Variance column catches pilferage.</li>
            <li>Expenses, Restaurant POS (top items, hourly heatmap), Stock, Portfolio.</li>
          </ul>
        </Section>

        <Section icon={ClockCounterClockwiseIcon} title="Undo Check-in / Check-out">
          <p>Made a mistake? Every checked-in row has a ↺ button that reverts to booked and frees the room. Checked-out rows can also be undone — the auto-created housekeeping task and draft invoice are cleaned up automatically.</p>
          <p className="text-[11px] italic">All undo actions are logged in the Audit trail.</p>
        </Section>

        <Section icon={ShieldCheckIcon} title="Roles & Permissions">
          <p>Six roles with granular per-section RW permissions:</p>
          <ul className="list-disc pl-5 space-y-1 text-xs">
            <li><b>Admin</b> — full access</li>
            <li><b>Manager</b> — everything except platform + master</li>
            <li><b>Front Desk</b> — reservations, guests, invoices, POS bill-to-room</li>
            <li><b>Housekeeping</b> — tasks + rooms status only</li>
            <li><b>Restaurant Manager</b> — POS-only (POS menu + stock + orders)</li>
            <li><b>Service / Waiter</b> — POS order-taking only</li>
          </ul>
          <p>Admin Console → Users lets you fine-tune the matrix per staff. Route guards redirect blocked users to their allowed home screen.</p>
        </Section>

        <Section icon={GearSixIcon} title="Admin Console">
          <p>Vertical sidebar menu at <Kbd>/admin</Kbd>:</p>
          <ul className="list-disc pl-5 space-y-1 text-xs">
            <li><b>Group</b> — brand + logo + KYC (ID card mandatory) toggle</li>
            <li><b>Invoice</b> — number series, footer, default tax</li>
            <li><b>Guest emails</b> — booking confirmation templates</li>
            <li><b>Email</b> + <b>WhatsApp</b> — provider keys</li>
            <li><b>GST</b> — CA email + monthly schedule</li>
            <li><b>Meal Plans</b> — EP / CP / MAP / AP rates + extra bed + CNB rate</li>
            <li><b>Sidebar Order</b> — reorder the left-nav for everyone</li>
            <li><b>Approval</b> — expense approval workflow</li>
            <li><b>Tables</b> + <b>Printing</b> — POS floor plan + thermal printer IPs</li>
            <li><b>Backups</b> — daily MongoDB snapshots (master-only restore)</li>
            <li><b>OTA</b> — Booking.com, MMT, Expedia, Airbnb, Agoda, Goibibo, <b>Yatra</b></li>
            <li><b>Master</b> — owner-only property CRUD + user role</li>
          </ul>
        </Section>

        <Section icon={CloudArrowUpIcon} title="Offline Mode" badge="Reworked">
          <p>The whole PMS is a Progressive Web App — <b>keeps working even without internet</b>.</p>
          <ul className="list-disc pl-5 space-y-1 text-xs">
            <li>On login the app warms an offline cache: properties, settings, rooms, guests, POS menu + stock, quotations, reservations, invoices.</li>
            <li>Any change made while offline (POST/PUT/PATCH/DELETE) is queued in IndexedDB — <b>including new features</b>: quotations, meal plan bookings, checkout&amp;bill extras, hall per-plate bookings, undo checkin/out.</li>
            <li>The <b>cloud icon</b> in the top-right of the sidebar shows queue count. Click it to see pending changes; on reconnect the queue auto-flushes in order.</li>
            <li>Screens keep reading from cache — stale-while-revalidate ensures the UI is never blank.</li>
          </ul>
          <p className="text-[11px] italic">Bump the SW cache version to force a refresh on all devices.</p>
        </Section>

        <Section icon={PlugsIcon} title="OTA Sync (Channels)">
          <p>Currently the Channels page + Admin Console OTA tab store credentials only. Real inventory / rate push is on the roadmap. Yatra is now in the list.</p>
        </Section>

        <Section icon={IdentificationCardIcon} title="KYC / ID Card">
          <p>Admin Console → Group has the <b>ID card mandatory for check-in</b> toggle. When on, front-desk cannot check a guest in without both a photo and ID card image uploaded. Recommended: on, for FRRO / GST compliance.</p>
        </Section>

        <Section icon={ArrowClockwiseIcon} title="Backups & Restore">
          <p>MongoDB is snapshotted daily via APScheduler. Last 7 days retained. Master owner can restore from Admin Console → Backups. Admins see the list but cannot restore.</p>
          <p className="text-[11px] italic">Every restore auto-creates a pre-restore snapshot so you can always rewind further.</p>
        </Section>

        <Section icon={CrownSimpleIcon} title="Master Owner (Protected)" badge="Critical">
          <p>The master owner is the platform's <b>root account</b>. Only the master can:</p>
          <ul className="list-disc pl-5 space-y-1 text-xs">
            <li>Delete guests, invoices, reservations, rooms, properties</li>
            <li>Restore any Mongo backup</li>
            <li>Transfer master ownership to another user</li>
            <li>Run a full factory reset (handover)</li>
          </ul>
          <p><b>The master account is non-deletable</b> — even admins cannot remove it. To change owner, use Admin Console → Master → <i>Transfer ownership</i>.</p>
        </Section>

        <Section icon={TrashIcon} title="Factory Reset (Handover)" badge="Master only">
          <p>When handing the platform to a new owner, master goes to Admin Console → Master → <b>Reset all data</b>. Steps:</p>
          <ol className="list-decimal pl-5 space-y-1 text-xs">
            <li>Type the phrase <code className="bg-muted px-1 rounded">DELETE ALL DATA</code> to confirm.</li>
            <li>Choose whether to keep properties + rooms (recommended for handover).</li>
            <li>A pre-reset backup is auto-created before any collection is wiped.</li>
            <li>Every reservation, invoice, POS order, guest, expense, task, audit and quotation is removed. Master + settings are retained.</li>
          </ol>
        </Section>

        <Section icon={WarningIcon} title="Error Management">
          <p>The platform now uses a <b>global error surface</b>:</p>
          <ul className="list-disc pl-5 space-y-1 text-xs">
            <li>Backend returns clean JSON with <code>{"{detail: string}"}</code> — validation errors are humanised (e.g. <i>"property_id: Field required"</i>).</li>
            <li>Frontend axios interceptor auto-shows a red toast for any 4xx/5xx unless the page handled it locally.</li>
            <li>401 → auto-redirect to login. 403 → "You don't have permission". 500 → "Server error, please retry".</li>
            <li>Network errors are recognised — offline mutations queue silently, no scary toast.</li>
          </ul>
        </Section>

        <Section icon={DeviceMobileIcon} title="Mobile & Accessibility">
          <p>Fully responsive PWA:</p>
          <ul className="list-disc pl-5 space-y-1 text-xs">
            <li>On phones the sidebar collapses into a top bar with a hamburger drawer.</li>
            <li>All modals scroll vertically inside the viewport — no more content trapped off-screen.</li>
            <li>Housekeepers can add <Kbd>/my-tasks</Kbd> to their home screen and receive push alerts.</li>
            <li>Every touch target is at least 40 px tall.</li>
          </ul>
        </Section>
      </div>

      <div className="mt-6 rounded-md border border-dashed border-border p-4 text-xs text-muted-foreground">
        <b>Need something else?</b> Ping the platform admin. All feature requests and bug reports are logged with a timestamp on the Audit page (<Kbd>/audit</Kbd>).
      </div>
    </div>
  );
}
